import React, { useEffect, useRef, useState } from "react";
import { FiChevronLeft, FiChevronRight, FiBox } from "react-icons/fi";
import axios from "../../../Utils/axiosInstance";
import { Link } from "react-router-dom";

const Categories = () => {
  const scrollRef = useRef();
  const specialScrollRef = useRef();
  const swadeshiScrollRef = useRef();

  const [categories, setCategories] = useState([]);
  const [plantCategories, setPlantCategories] = useState([]);
  const [swadeshiCategories, setSwadeshiCategories] = useState([]);
  const [specialVarieties, setSpecialVarieties] = useState([]);
  const [swadeshiVarieties, setSwadeshiVarieties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const shouldCenterCards = categories.length > 0 && categories.length < 5;

  const scroll = (dir) => {
    scrollRef.current?.scrollBy({
      left: dir === "left" ? -200 : 200,
      behavior: "smooth",
    });
  };

  const scrollSpecial = (dir) => {
    specialScrollRef.current?.scrollBy({
      left: dir === "left" ? -280 : 280,
      behavior: "smooth",
    });
  };

  const scrollSwadeshi = (dir) => {
    swadeshiScrollRef.current?.scrollBy({
      left: dir === "left" ? -280 : 280,
      behavior: "smooth",
    });
  };

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(false);

      const [popularRes, plantRes, swadeshiRes] = await Promise.all([
        axios.get("/category/get-all", {
          params: { special: false },
        }),
        axios.get("/category/get-all", {
          params: { specialType: "plant_extracts", limit: 6 },
        }),
        axios.get("/category/get-all", {
          params: { specialType: "swadeshi", limit: 6 },
        }),
      ]);

      const popularData = popularRes?.data?.data || [];
      const plantCats = plantRes?.data?.data || [];
      const swadeshiCats = swadeshiRes?.data?.data || [];

      setCategories(popularData);
      setPlantCategories(plantCats);
      setSwadeshiCategories(swadeshiCats);

      const fetchVarietiesForCategories = async (catList) => {
        if (!catList || catList.length === 0) return [];
        const requests = catList.map((category) =>
          axios.get("/product-variety/get-all", {
            params: {
              categoryId: category._id,
              isActive: true,
              limit: 6,
              populate: true,
            },
          }).catch((err) => {
            console.error("Error fetching varieties for category", category._id, err);
            return { data: { data: [] } };
          })
        );
        const responses = await Promise.all(requests);
        return responses.flatMap((resp, index) => {
          const varieties = resp?.data?.data || [];
          return varieties.map((variety) => ({
            ...variety,
            parentCategory: catList[index],
          }));
        });
      };

      const [plantData, swadeshiData] = await Promise.all([
        fetchVarietiesForCategories(plantCats),
        fetchVarietiesForCategories(swadeshiCats),
      ]);

      setSpecialVarieties(plantData);
      setSwadeshiVarieties(swadeshiData);
    } catch (err) {
      console.error("Categories fetch error:", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // Determine display items for Plant Extracts: varieties if available, else categories
  const hasPlantSection = specialVarieties.length > 0 || plantCategories.length > 0;
  const plantDisplayItems = specialVarieties.length > 0 ? specialVarieties : plantCategories;
  const isPlantVarieties = specialVarieties.length > 0;

  // Determine display items for Swadeshi: varieties if available, else categories
  const hasSwadeshiSection = swadeshiVarieties.length > 0 || swadeshiCategories.length > 0;
  const swadeshiDisplayItems = swadeshiVarieties.length > 0 ? swadeshiVarieties : swadeshiCategories;
  const isSwadeshiVarieties = swadeshiVarieties.length > 0;

  return (
    <div className="w-full pb-14 md:pb-16 px-4 md:px-8">
      <div className="2xl:max-w-7xl mx-auto">
        {/* TITLE - POPULAR CATEGORIES */}
        <div className="text-center mb-12">
          <div className="flex flex-col items-center gap-2">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold bg-clip-text text-transparent bg-gradient-to-r from-[#3D0F1B] to-subPrimaryColor py-2">
              Our Popular Categories
            </h2>
            <p className="text-gray-500 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
              Explore our wide selection of high-quality products sourced
              directly from trusted suppliers
            </p>
          </div>
        </div>

        {/* LOADING SKELETON */}
        {loading && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="bg-gray-100 rounded-2xl h-44 animate-pulse"
              />
            ))}
          </div>
        )}

        {/* ERROR STATE */}
        {error && !loading && (
          <div className="text-center py-12">
            <p className="text-gray-500">Failed to load categories</p>
            <button
              onClick={fetchCategories}
              className="mt-3 text-sm text-primaryColor underline hover:text-subPrimaryColor"
            >
              Try again
            </button>
          </div>
        )}

        {/* CONTENT */}
        {!loading && !error && (
          <>
            {/* POPULAR CATEGORIES GRID / SCROLL */}
            {categories.length > 0 && (
              <>
                <div
                  ref={scrollRef}
                  className={`flex gap-4 overflow-x-auto scroll-smooth pb-2 snap-x snap-mandatory ${
                    shouldCenterCards ? "md:justify-center" : ""
                  }`}
                >
                  {categories.map((item, i) => (
                    <Link
                      to={`/products?category=${item._id}`}
                      key={item._id || i}
                      className="group w-[85%] shrink-0 sm:w-[49%] lg:w-[24%] snap-center overflow-hidden rounded-[1.5rem] bg-white hover:shadow-lg shadow-sm transition duration-300"
                    >
                      <div className="relative h-44 overflow-hidden bg-gradient-to-br from-[#FFF4F6] to-[#FDEFF2] sm:h-48">
                        {item.img ? (
                          <img
                            src={item.img}
                            alt={item.name}
                            className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-110 group-hover:translate-y-[-5px]"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#FFEDEE] to-[#F9F0F2]">
                            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-white/80 shadow-sm">
                              <FiBox className="h-9 w-9 text-primaryColor" />
                            </div>
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-[#3D0F1B]/20 via-transparent to-transparent" />
                      </div>

                      <div className="px-4 py-5 text-center sm:px-5">
                        <h3 className="text-lg font-semibold text-[#3D0F1B] group-hover:text-primaryColor">
                          {item.name}
                        </h3>
                        <p className="mt-1 line-clamp-2 text-sm text-gray-500">
                          {item.description || "Browse product catalog"}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>

                {/* POPULAR CONTROLS */}
                <div className="flex justify-center items-center gap-3 mt-8">
                  <button
                    onClick={() => scroll("left")}
                    className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-full bg-primaryBtnColor text-white hover:bg-primaryBtnHoverColor transition"
                  >
                    <FiChevronLeft />
                  </button>

                  <Link
                    to="/categories"
                    className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-primaryColor border-2 border-primaryColor rounded-full hover:bg-primaryColor hover:text-white transition-all duration-200 active:scale-95"
                  >
                    View All
                    <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold bg-primaryColor/10 rounded-full">
                      {categories.length}
                    </span>
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2.5}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M13 7l5 5-5 5M6 12h12"
                      />
                    </svg>
                  </Link>

                  <button
                    onClick={() => scroll("right")}
                    className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-full bg-primaryBtnColor text-white hover:bg-primaryBtnHoverColor transition"
                  >
                    <FiChevronRight />
                  </button>
                </div>
              </>
            )}

            {/* PLANT EXTRACTS SPECIAL CATEGORY SECTION */}
            {hasPlantSection && (
              <div className="mt-16">
                <div className="mb-8 text-center">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-semibold mb-2">
                    <span>🌿</span> Special Category
                  </div>
                  <h3 className="text-3xl md:text-4xl lg:text-5xl font-semibold bg-clip-text text-transparent bg-gradient-to-r from-[#3D0F1B] to-subPrimaryColor py-2">
                    Plant Extracts
                  </h3>
                  <p className="mx-auto mt-2 max-w-2xl text-sm leading-relaxed text-gray-500 md:text-base">
                    Discover our range of high-quality plant extracts, carefully sourced for their potency and purity.
                  </p>
                </div>

                <div className="px-0 md:px-0">
                  <div
                    ref={specialScrollRef}
                    className="flex gap-4 overflow-x-auto scroll-smooth pb-2 snap-x snap-mandatory"
                  >
                    {plantDisplayItems.map((item, i) => {
                      const href = isPlantVarieties
                        ? `/products?category=${item.productId?.subCategoryId?.categoryId?._id || item.parentCategory?._id}&subCategory=${item.productId?.subCategoryId?._id}&product=${item.productId?._id}&variety=${item._id}`
                        : `/products?category=${item._id}`;

                      const title = isPlantVarieties ? item.varietyName : item.name;
                      const subtitle = isPlantVarieties
                        ? (item.productId?.name || item.parentCategory?.name)
                        : (item.description || "Browse product varieties");

                      return (
                        <Link
                          to={href}
                          key={item._id || i}
                          className="group w-[85%] shrink-0 sm:w-[49%] lg:w-[24%] snap-center overflow-hidden rounded-[1.5rem] bg-white hover:shadow-lg shadow-sm transition duration-300"
                        >
                          <div className="relative h-44 overflow-hidden bg-gradient-to-br from-[#FFF4F6] to-[#FDEFF2] sm:h-48">
                            {item.img ? (
                              <img
                                src={item.img}
                                alt={title}
                                className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-110 group-hover:translate-y-[-5px]"
                              />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#FFEDEE] to-[#F9F0F2]">
                                <div className="flex h-20 w-20 items-center justify-center rounded-full bg-white/80 shadow-sm">
                                  <FiBox className="h-9 w-9 text-primaryColor" />
                                </div>
                              </div>
                            )}
                            <div className="absolute inset-0 bg-gradient-to-t from-[#3D0F1B]/20 via-transparent to-transparent" />
                          </div>

                          <div className="px-4 py-5 text-center sm:px-5">
                            <h4 className="text-lg font-semibold text-[#3D0F1B] group-hover:text-primaryColor">
                              {title}
                            </h4>
                            <p className="mt-1 text-sm text-gray-500 line-clamp-1">
                              {subtitle}
                            </p>
                          </div>
                        </Link>
                      );
                    })}
                  </div>

                  <div className="mt-8 flex items-center justify-center gap-3">
                    <button
                      onClick={() => scrollSpecial("left")}
                      className="flex h-8 w-8 items-center justify-center rounded-full bg-primaryBtnColor text-white transition hover:bg-primaryBtnHoverColor md:h-9 md:w-9"
                    >
                      <FiChevronLeft />
                    </button>

                    <Link
                      to="/special-varieties?type=plant_extracts"
                      className="flex items-center gap-2 rounded-full border-2 border-primaryColor px-4 py-2 text-sm font-semibold text-primaryColor transition-all duration-200 hover:bg-primaryColor hover:text-white active:scale-95"
                    >
                      View All Plant Extracts
                      <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primaryColor/10 text-xs font-bold">
                        {plantDisplayItems.length}
                      </span>
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5-5 5M6 12h12" />
                      </svg>
                    </Link>

                    <button
                      onClick={() => scrollSpecial("right")}
                      className="flex h-8 w-8 items-center justify-center rounded-full bg-primaryBtnColor text-white transition hover:bg-primaryBtnHoverColor md:h-9 md:w-9"
                    >
                      <FiChevronRight />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* SWADESHI (HANDICRAFTS & HANDLOOMS) SPECIAL CATEGORY SECTION */}
            {hasSwadeshiSection && (
              <div className="mt-16">
                <div className="mb-8 text-center">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200 text-xs font-semibold mb-2">
                    <span>🎨</span> Swadeshi Special Category
                  </div>
                  <h3 className="text-3xl md:text-4xl lg:text-5xl font-semibold bg-clip-text text-transparent bg-gradient-to-r from-[#3D0F1B] to-subPrimaryColor py-2">
                    SWADESHI (HANDICRAFTS & HANDLOOMS)
                  </h3>
                  <p className="mx-auto mt-2 max-w-2xl text-sm leading-relaxed text-gray-500 md:text-base">
                    Explore authentic handcrafted and handloom treasures crafted with traditional heritage and artisanal skill.
                  </p>
                </div>

                <div className="px-0 md:px-0">
                  <div
                    ref={swadeshiScrollRef}
                    className="flex gap-4 overflow-x-auto scroll-smooth pb-2 snap-x snap-mandatory"
                  >
                    {swadeshiDisplayItems.map((item, i) => {
                      const href = isSwadeshiVarieties
                        ? `/products?category=${item.productId?.subCategoryId?.categoryId?._id || item.parentCategory?._id}&subCategory=${item.productId?.subCategoryId?._id}&product=${item.productId?._id}&variety=${item._id}`
                        : `/products?category=${item._id}`;

                      const title = isSwadeshiVarieties ? item.varietyName : item.name;
                      const subtitle = isSwadeshiVarieties
                        ? (item.productId?.name || item.parentCategory?.name)
                        : (item.description || "Authentic Handcrafted & Handloom collection");

                      return (
                        <Link
                          to={href}
                          key={item._id || i}
                          className="group w-[85%] shrink-0 sm:w-[49%] lg:w-[24%] snap-center overflow-hidden rounded-[1.5rem] bg-white hover:shadow-lg shadow-sm transition duration-300"
                        >
                          <div className="relative h-44 overflow-hidden bg-gradient-to-br from-[#FFF4F6] to-[#FDEFF2] sm:h-48">
                            {item.img ? (
                              <img
                                src={item.img}
                                alt={title}
                                className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-110 group-hover:translate-y-[-5px]"
                              />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#FFEDEE] to-[#F9F0F2]">
                                <div className="flex h-20 w-20 items-center justify-center rounded-full bg-white/80 shadow-sm">
                                  <FiBox className="h-9 w-9 text-primaryColor" />
                                </div>
                              </div>
                            )}
                            <div className="absolute inset-0 bg-gradient-to-t from-[#3D0F1B]/20 via-transparent to-transparent" />
                            <span className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm px-2.5 py-0.5 rounded-full text-[11px] font-semibold text-amber-800 shadow-xs">
                              Swadeshi
                            </span>
                          </div>

                          <div className="px-4 py-5 text-center sm:px-5">
                            <h4 className="text-lg font-semibold text-[#3D0F1B] group-hover:text-primaryColor">
                              {title}
                            </h4>
                            <p className="mt-1 text-sm text-gray-500 line-clamp-1">
                              {subtitle}
                            </p>
                          </div>
                        </Link>
                      );
                    })}
                  </div>

                  <div className="mt-8 flex items-center justify-center gap-3">
                    <button
                      onClick={() => scrollSwadeshi("left")}
                      className="flex h-8 w-8 items-center justify-center rounded-full bg-primaryBtnColor text-white transition hover:bg-primaryBtnHoverColor md:h-9 md:w-9"
                    >
                      <FiChevronLeft />
                    </button>

                    <Link
                      to="/special-varieties?type=swadeshi"
                      className="flex items-center gap-2 rounded-full border-2 border-primaryColor px-4 py-2 text-sm font-semibold text-primaryColor transition-all duration-200 hover:bg-primaryColor hover:text-white active:scale-95"
                    >
                      View All Swadeshi
                      <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primaryColor/10 text-xs font-bold">
                        {swadeshiDisplayItems.length}
                      </span>
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5-5 5M6 12h12" />
                      </svg>
                    </Link>

                    <button
                      onClick={() => scrollSwadeshi("right")}
                      className="flex h-8 w-8 items-center justify-center rounded-full bg-primaryBtnColor text-white transition hover:bg-primaryBtnHoverColor md:h-9 md:w-9"
                    >
                      <FiChevronRight />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Categories;
